/**
 *  @class
 *  @function VideoControls
 */
if (!customElements.get('video-controls')) {
  class VideoControls extends HTMLElement {
    constructor() {
      super();
      this._onPlayPause = this.playPause.bind(this);
      this._onMuteUnmute = this.muteUnmute.bind(this);
      this._onPlay = () => this.syncUi();
      this._onPause = () => this.syncUi();
      this._onVolume = () => this.syncUi();
      this._blockDrag = this.blockDrag.bind(this);
    }

    connectedCallback() {
      this.root = this.closest('.slideshow__slide-video-bg') || this.parentElement;
      this.carousel = this.closest('slide-show, .shoppable-video-reels--carousel');
      this.video = this.root ? this.root.querySelector('video') : null;
      this.sound_toggle = this.querySelector('.video-controls--sound');
      this.play_toggle = this.querySelector('.video-controls--play-pause');
      if (!this.video) return;
      // Start paused — user must press play.
      this.video.dataset.userPaused = 'true';
      this.video.pause();
      this.setEventListeners();
      this.syncUi();
    }

    disconnectedCallback() {
      this.unbindControl(this.play_toggle, this._onPlayPause);
      this.unbindControl(this.sound_toggle, this._onMuteUnmute);
      if (this.video) {
        this.video.removeEventListener('play', this._onPlay);
        this.video.removeEventListener('pause', this._onPause);
        this.video.removeEventListener('volumechange', this._onVolume);
      }
    }

    bindControl(el, clickHandler) {
      if (!el) return;
      ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'touchend'].forEach((type) => {
        el.addEventListener(type, this._blockDrag, true);
      });
      el.addEventListener('click', clickHandler, true);
    }

    unbindControl(el, clickHandler) {
      if (!el) return;
      ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'touchend'].forEach((type) => {
        el.removeEventListener(type, this._blockDrag, true);
      });
      el.removeEventListener('click', clickHandler, true);
    }

    setEventListeners() {
      this.bindControl(this.play_toggle, this._onPlayPause);
      this.bindControl(this.sound_toggle, this._onMuteUnmute);
      this.video.addEventListener('play', this._onPlay);
      this.video.addEventListener('pause', this._onPause);
      this.video.addEventListener('volumechange', this._onVolume);
    }

    blockDrag(e) {
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    }

    syncUi() {
      if (!this.video) return;
      if (this.play_toggle) {
        this.play_toggle.classList.toggle('paused', this.video.paused);
      }
      if (this.sound_toggle) {
        this.sound_toggle.classList.toggle('muted', this.video.muted);
      }
    }

    pauseOtherVideos() {
      const scope = this.carousel || document;
      scope.querySelectorAll('video').forEach((vid) => {
        if (vid === this.video) return;
        vid.dataset.userPaused = 'true';
        vid.pause();
        const root = vid.closest('.slideshow__slide-video-bg') || vid.parentElement;
        const toggle = root && root.querySelector('.video-controls--play-pause');
        if (toggle) toggle.classList.add('paused');
      });
    }

    playPause(e) {
      this.blockDrag(e);
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (!this.video) return;

      if (this.video.paused) {
        // Only one reel playing at a time.
        this.pauseOtherVideos();
        this.video.dataset.userPaused = 'false';
        const playPromise = this.video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(() => this.syncUi()).catch(() => this.syncUi());
        } else {
          this.syncUi();
        }
      } else {
        this.video.dataset.userPaused = 'true';
        this.video.pause();
        this.syncUi();
      }
    }

    muteUnmute(e) {
      this.blockDrag(e);
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.syncUi();
    }
  }
  customElements.define('video-controls', VideoControls);
}
